/**
 * realtime.ts — one shared realtime connection for authoritative refresh.
 *
 * Why this exists (Incident #50): the patient's My Bookings screen never learned
 * that its booking had been confirmed. NotificationBell already had a socket,
 * but re-creating one per screen would mean several connections per page. This
 * module owns ONE refcounted connection shared by every screen that needs to
 * re-read authoritative state, and tears it down when the last one unmounts.
 *
 * Contract:
 *  - Socket.IO is an ACCELERATION layer only. It never carries authoritative
 *    state; a subscriber's reaction is always to re-read the API.
 *  - `undefined` type = "resynchronise" (socket (re)connected). A reconnect must
 *    surface anything missed while offline, which is precisely why connect is
 *    forwarded rather than swallowed.
 *  - A listener that throws cannot break the others.
 *  - If realtime is unavailable (no token, offline), subscribing still succeeds:
 *    the screens also refresh on window focus, and the API stays authoritative.
 */

import { connectSocket } from './api';

export type RealtimeListener = (type: string | undefined) => void;

/**
 * Authoritative consultation session, as returned by
 * `POST /api/bookings/:id/consultation/end`.
 * The client uses this to render an IMMUTABLE final duration — it must never be
 * combined with, or replaced by, a locally-invented value.
 */
export interface ConsultationSessionPayload {
  bookingId: number;
  status?: string;
  startedAt?: string | null;
  endedAt?: string | null;
  durationSeconds?: number | null;
  isEnded?: boolean;
}

export type ConsultationEndedListener = (session: ConsultationSessionPayload) => void;

/**
 * Authoritative "the clinical encounter is complete" signal.
 * Distinct from `consultation:ended`, which only means the current VIDEO session
 * closed. Only a clinician's completion emits this, and it is terminal.
 */
export type EncounterCompletedListener = (session: ConsultationSessionPayload) => void;

/**
 * An incoming-call invite: the OTHER participant has OPENED the consultation and
 * the recipient is not in the room. Payload is validated by the consumer
 * (`utils/incomingCall.parseIncomingCall`) before anything is rendered.
 */
export type IncomingCallListener = (payload: unknown) => void;

/**
 * An explicit "the other side declined your call" disposition.
 * Payload is validated by the consumer (`utils/callDecline.parseCallDeclined`)
 * before anything is rendered.
 */
export type CallDeclinedListener = (payload: unknown) => void;

/**
 * The shared connection, plus the one write this module performs.
 * Decline is the ONLY client→server emit here: the recipient is by definition
 * NOT in the consultation room, so their room-less connection is the only one
 * that can report the refusal. The server authorizes it from the AUTHENTICATED
 * socket (never from the payload) and against its own open session.
 */
type SharedSocket = {
  on: (e: string, h: (d: unknown) => void) => void;
  emit: (e: string, payload: unknown, ack?: (response: any) => void) => void;
  disconnect: () => void;
};

let sharedSocket: SharedSocket | null = null;
let connecting: Promise<unknown> | null = null;
const listeners = new Set<RealtimeListener>();
const consultationListeners = new Set<ConsultationEndedListener>();
const encounterCompletedListeners = new Set<EncounterCompletedListener>();
const incomingCallListeners = new Set<IncomingCallListener>();
const callDeclinedListeners = new Set<CallDeclinedListener>();
let subscriberCount = 0;

/**
 * Release one subscriber and tear the socket down when nobody needs it.
 * Shared by every subscription kind so the refcount can never drift.
 */
function releaseSubscriber() {
  subscriberCount -= 1;
  if (subscriberCount <= 0) {
    try { sharedSocket?.disconnect(); } catch { /* ignore */ }
    sharedSocket = null;
    connecting = null;
    subscriberCount = 0;
  }
}

/**
 * Attach a subscriber to the shared socket, connecting it on first use.
 * Returns the unsubscribe function that must be called on unmount.
 */
function addSubscriber(): () => void {
  subscriberCount += 1;
  ensureSocket().catch(() => {
    // Realtime unavailable: callers keep working from the authoritative API.
  });

  let released = false;
  return () => {
    if (released) return;
    released = true;
    releaseSubscriber();
  };
}

function emit(type: string | undefined) {
  // Copy first so a listener unsubscribing during dispatch cannot skip others.
  for (const listener of Array.from(listeners)) {
    try {
      listener(type);
    } catch {
      // One screen's failure must never starve the others.
    }
  }
}

function ensureSocket(): Promise<unknown> {
  if (sharedSocket) return Promise.resolve(sharedSocket);
  if (!connecting) {
    connecting = connectSocket()
      .then((socket) => {
        sharedSocket = socket as unknown as typeof sharedSocket;
        socket.on('notification', (data: unknown) => {
          const type = (data as { type?: unknown } | null)?.type;
          emit(typeof type === 'string' ? type : undefined);
        });
        // Authoritative consultation ended (either participant). Forwarded so
        // every open room can stop its timer from server-confirmed state.
        socket.on('consultation:ended', (data: unknown) => reportConsultationEnded(data));
        socket.on('consultation:completed', (data: unknown) => reportEncounterCompleted(data));
        // The invite is delivered to this user's own `user:<uid>` room, so this
        // shared connection receives it from ANY screen in the application —
        // which is the point: the recipient is, by definition, not in the room.
        socket.on('consultation:incoming', (data: unknown) => reportIncomingCall(data));
        // The caller's counterparty declined. Delivered on the CALLER's own
        // `user:<uid>` room, so it reaches the screen that is actually waiting.
        socket.on('consultation:declined', (data: unknown) => reportCallDeclined(data));
        // Fires on first connect AND on every reconnect — both are moments the
        // authoritative state must be re-read (a reconnect may have missed
        // events while the tab was offline).
        socket.on('connect', () => emit(undefined));
        if (subscriberCount <= 0) {
          // Everyone unmounted while we were connecting — do not leak a socket.
          try { socket.disconnect(); } catch { /* ignore */ }
          sharedSocket = null;
          connecting = null;
        }
        return socket;
      })
      .catch((err) => {
        connecting = null; // allow a later subscriber to retry
        throw err;
      });
  }
  return connecting;
}

/**
 * Subscribe to realtime booking invalidation signals. Returns an unsubscribe
 * function that must be called on unmount.
 */
export function subscribeToBookingEvents(listener: RealtimeListener): () => void {
  listeners.add(listener);
  const release = addSubscriber();

  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    listeners.delete(listener);
    release();
  };
}

/**
 * Forward a `consultation:ended` payload into this module's listeners.
 *
 * MUST be callable from the connection that owns room membership, not just from
 * this module's own socket. `connectSocket()` opens a NEW Socket.IO connection
 * per call, and the one that emits `consultation:join` belongs to
 * `useWebRTCConsultation` — NOT the connection this module listens on. The
 * server broadcasts the end to the ROOM only, so without this forward the
 * event is delivered to a connection nobody is listening on and the remote
 * participant's timer keeps counting after the other side has ended.
 * (Reproduced in production — booking #43.)
 */
export function reportConsultationEnded(session: unknown) {
  if (!session || typeof session !== 'object') return;
  for (const listener of Array.from(consultationListeners)) {
    try {
      listener(session as ConsultationSessionPayload);
    } catch {
      // One room's failure must never starve the others.
    }
  }
}

/**
 * Subscribe to the authoritative "consultation ended" signal.
 * Because Socket.IO is only an acceleration layer, the room ALSO re-reads the
 * session endpoint on connect, so a missed event cannot leave a stale timer.
 */
export function subscribeToConsultationEnded(listener: ConsultationEndedListener): () => void {
  consultationListeners.add(listener);
  const release = addSubscriber();

  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    consultationListeners.delete(listener);
    release();
  };
}

/**
 * Forward a `consultation:completed` payload into this module's listeners.
 * Same room-membership caveat as `reportConsultationEnded` — callable from the
 * connection that actually joined the consultation room.
 */
export function reportEncounterCompleted(session: unknown) {
  if (!session || typeof session !== 'object') return;
  for (const listener of Array.from(encounterCompletedListeners)) {
    try {
      listener(session as ConsultationSessionPayload);
    } catch {
      // One room's failure must never starve the others.
    }
  }
}

/**
 * Subscribe to the authoritative "encounter completed" signal.
 * The room also re-reads the session endpoint on focus, so a missed event
 * cannot leave a completed encounter looking rejoinable.
 */
export function subscribeToEncounterCompleted(listener: EncounterCompletedListener): () => void {
  encounterCompletedListeners.add(listener);
  const release = addSubscriber();

  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    encounterCompletedListeners.delete(listener);
    release();
  };
}

/**
 * Forward a `consultation:incoming` payload into this module's listeners.
 * Callable from whichever connection actually received it (same room-membership
 * caveat as the other forwards).
 */
export function reportIncomingCall(payload: unknown) {
  if (!payload || typeof payload !== 'object') return;
  for (const listener of Array.from(incomingCallListeners)) {
    try {
      listener(payload);
    } catch {
      // One screen's failure must never starve the others.
    }
  }
}

/**
 * Subscribe to incoming-call invites. Subscribing also keeps the shared socket
 * ALIVE (refcounted), which is what guarantees the invite has a connection to
 * arrive on anywhere in the application.
 */
export function subscribeToIncomingCall(listener: IncomingCallListener): () => void {
  incomingCallListeners.add(listener);
  const release = addSubscriber();

  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    incomingCallListeners.delete(listener);
    release();
  };
}

/**
 * Forward a `consultation:declined` payload into this module's listeners.
 * Callable from whichever connection actually received it (the shared socket is
 * joined to the user's own room, which is where the server addresses it).
 */
export function reportCallDeclined(payload: unknown) {
  if (!payload || typeof payload !== 'object') return;
  for (const listener of Array.from(callDeclinedListeners)) {
    try {
      listener(payload);
    } catch {
      // One screen's failure must never starve the others.
    }
  }
}

/**
 * Subscribe to call-decline dispositions. Subscribing also keeps the shared
 * socket alive, which is what guarantees a running consultation room has a
 * connection for the disposition to arrive on.
 */
export function subscribeToCallDeclined(listener: CallDeclinedListener): () => void {
  callDeclinedListeners.add(listener);
  const release = addSubscriber();

  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    callDeclinedListeners.delete(listener);
    release();
  };
}

/**
 * Tell the server that this participant declines the ringing call.
 *
 * Resolves with the server's verdict so a UI can distinguish "the caller has
 * been told" from "the disposition was refused" (a stale invite, a finished
 * consultation). It deliberately does NOT throw: the local ring has already
 * stopped, and a realtime hiccup must never turn a decline into an error
 * dialog. `reasonCode` is a stable code; `note` is optional free text (OTHER).
 */
export async function declineConsultationCall({
  bookingId,
  sessionId,
  reasonCode,
  note,
}: {
  bookingId: number;
  sessionId: number | null;
  reasonCode: string;
  note?: string | null;
}): Promise<{ ok: boolean; error?: string }> {
  let socket: SharedSocket;
  try {
    socket = (await ensureSocket()) as SharedSocket;
  } catch {
    return { ok: false, error: 'realtime-unavailable' };
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: { ok: boolean; error?: string }) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    // The server acks immediately (no I/O beyond one row read); a missing ack
    // means the connection is gone and the caller will simply time out.
    const timer = setTimeout(() => finish({ ok: false, error: 'decline-timeout' }), 5000);
    try {
      socket.emit(
        'consultation:decline',
        { bookingId, sessionId, reasonCode, note: note || null },
        (ack: any) => finish(ack?.ok ? { ok: true } : { ok: false, error: ack?.error || 'decline-rejected' })
      );
    } catch {
      finish({ ok: false, error: 'decline-emit-failed' });
    }
  });
}
